import { UUID } from "@bitory-gmbh/nidavellir";
import {
  BaseDataSubdomain,
  useDefaultBaseDataDeleteMutation,
  useDefaultBaseDataListQuery,
  useDefaultBaseDataPutMutation,
} from "@/services/backend/shared/base-data-kit/api/default-base-data-api";
import { Checkbox } from "@/shared/components/ui/checkbox";
import { parseRTKQueryError } from "@/shared/components/errors/parse-r-t-k-query-error";
import { useToast } from "@/shared/hooks/use-toast";
import { CheckedState } from "@radix-ui/react-checkbox";

export interface DefaultBaseDataProps {
  /** The backend subdomain the defaults are mounted under, e.g. "htz". */
  subdomain: BaseDataSubdomain;
  /** The `basedatakit.Default` key, e.g. "htz.summary.baseData". */
  dataKey: string;
  companyId: UUID;
  /** The base data instance this refers to. */
  id: UUID;
}

/**
 * Reads whether the base data instance is the default of its company.
 *
 * All callers for the same company and key share one request, so a table
 * rendering this per row issues a single query.
 */
export function useIsDefaultBaseData({
  subdomain,
  dataKey,
  companyId,
  id,
}: DefaultBaseDataProps) {
  const { data, isLoading } = useDefaultBaseDataListQuery({
    subdomain,
    company: { active: true, values: [companyId] },
    key: { active: true, values: [dataKey] },
  });

  return {
    isDefault: data?.items.some((d) => d.id === id) ?? false,
    isLoading,
  };
}

/**
 * Marks a base data instance as the one its company instantiates from —
 * "aktiv" in the UI.
 *
 * A checkbox, not a radio: the backend upserts the default per company and
 * key, so checking another instance clears this one server-side.
 */
export function DefaultBaseDataCheckbox({
  subdomain,
  dataKey,
  companyId,
  id,
}: DefaultBaseDataProps) {
  const { isDefault } = useIsDefaultBaseData({
    subdomain,
    dataKey,
    companyId,
    id,
  });

  const [put, { isLoading: isPutting, error: putError, reset: resetPut }] =
    useDefaultBaseDataPutMutation();
  const [
    remove,
    { isLoading: isRemoving, error: removeError, reset: resetRemove },
  ] = useDefaultBaseDataDeleteMutation();

  const { toast } = useToast();

  if (putError) {
    toast({ ...parseRTKQueryError(putError), variant: "destructive" });
    resetPut();
  }
  if (removeError) {
    toast({ ...parseRTKQueryError(removeError), variant: "destructive" });
    resetRemove();
  }

  const isMutating = isPutting || isRemoving;

  const onCheckedChange = (newState: CheckedState) => {
    if (isMutating) {
      return;
    }

    switch (newState) {
      case true:
        put({ subdomain, companyId, key: dataKey, id });
        break;
      case false:
        remove({ subdomain, companyId, key: dataKey });
        break;
      default:
        break;
    }
  };

  return (
    <Checkbox
      checked={isDefault}
      onCheckedChange={onCheckedChange}
      className={isMutating ? "animate-pulse" : ""}
      disabled={isMutating}
    />
  );
}
